import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

export const useGameData = (gameId, currentUser) => {
    const [gameData, setGameData] = useState(null);
    const [quiz, setQuiz] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [finalResults, setFinalResults] = useState(null);

    useEffect(() => {
        if (!gameId || !currentUser?.uid) {
            console.log('Prerequisites not met:', { gameId, currentUserId: currentUser?.uid });
            return;
        }

        let isMounted = true;

        const loadGameData = async () => {
            try {
                // Load game session
                const gameDoc = await getDoc(doc(db, 'game_sessions', gameId));
                if (!gameDoc.exists()) {
                    setError('Game not found');
                    setLoading(false);
                    return;
                }

                const gameData = gameDoc.data();

                // Load quiz data
                const quizDoc = await getDoc(doc(db, 'quizzes', gameData.currentQuiz?.quizId));
                if (!quizDoc.exists()) {
                    setError('Quiz not found');
                    setLoading(false);
                    return;
                }

                const quizData = { id: quizDoc.id, ...quizDoc.data() };

                // Process player data
                const playerPromises = Object.entries(gameData.players || {})
                    // Filtere den Game Master aus, wenn er nicht teilnimmt
                    .filter(([id, player]) => {
                        if (gameData.gameMasterId === id && gameData.gameMasterParticipates === false) {
                            return false;
                        }
                        return true;
                    })
                    .map(async ([id, player]) => {
                        try {
                            const userDoc = await getDoc(doc(db, 'users', id));
                            const userData = userDoc.exists() ? userDoc.data() : {};
                            return {
                                id,
                                ...player,
                                hasRpmAvatar: userData.hasRpmAvatar || false,
                                rpmModelUrl: userData.rpmModelUrl || null,
                                rpmAvatarConfig: userData.rpmAvatarConfig || null,
                                rpmAvatarType: userData.rpmAvatarType || null
                            };
                        } catch (error) {
                            console.error('Error loading user data:', error);
                            return { id, ...player, hasRpmAvatar: false, rpmModelUrl: null };
                        }
                    });

                const enrichedPlayers = await Promise.all(playerPromises);
                const sortedPlayers = enrichedPlayers.sort((a, b) => {
                    if (b.score !== a.score) return b.score - a.score;
                    if (b.correctAnswers !== a.correctAnswers) return b.correctAnswers - a.correctAnswers;
                    return a.displayName.localeCompare(b.displayName);
                });

                if (isMounted) {
                    setGameData(gameData);
                    setQuiz(quizData);
                    setFinalResults(sortedPlayers);
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error loading game data:', error);
                if (isMounted) {
                    setError('Error loading game data');
                    setLoading(false);
                }
            }
        };

        loadGameData();
        return () => { isMounted = false; };
    }, [gameId, currentUser]);

    return { gameData, quiz, error, loading, finalResults };
};